import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Store, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorLogin = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/vendors/login`,
        { email, password },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(t('auth.vendor.welcomeBackToast'));
        navigate('/vendor-dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || t('auth.vendor.genericError');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Store className="h-12 w-12 text-emerald-600" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('auth.vendor.title')}</CardTitle>
          <CardDescription>{t('auth.vendor.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.vendor.businessEmail')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.vendor.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.vendor.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="vendor-login-password-toggle"
                >
                  {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? t('auth.vendor.signingIn') : t('auth.vendor.signIn')}
            </Button>
          </form>
          <div className="mt-6 space-y-3 text-center text-sm">
            <p className="text-gray-600">
              {t('auth.vendor.notVendorYet')}{' '}
              <Link to="/vendor-register" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                {t('auth.vendor.registerBusiness')}
              </Link>
            </p>
            <p className="text-gray-500 text-xs">
              {t('auth.vendor.areYouUser')}{' '}
              <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                {t('auth.vendor.userLogin')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorLogin;
